<template>
  <v-fab
    color="white"
    icon="mdi-menu"
    variant="plain"
    title="Show/Hide Sidebar"
    class="hamburger-button"
    @click="isCollapsed = !isCollapsed">
  </v-fab>
  <div class="sidebar" v-show="!isCollapsed">
    <!-- fixed-height search -->
    <v-text-field
      density="compact"
      class="mt-2 fixed-search"
      :model-value="searchQuery"
      label="search attribute keys or values..."
      append-icon="mdi-magnify"
      clearable
      hide-details
      @update:modelValue="$emit('update:searchQuery', $event)"
      @keyup.enter="$emit('search')"
    />

    <!-- Navigation -->
    <div class="view-switcher">
      <!-- Taxonomy-Specific Views -->
      <div class="view-group">
        <div class="group-header">
          Taxonomy-Specific
          <v-chip 
            v-if="taxonomyVersion" 
            size="x-small" 
            :color="taxonomyVersion === 'v2' ? 'warning' : 'success'"
            class="ml-1"
          >
            {{ taxonomyVersion?.toUpperCase() }}
          </v-chip>
        </div>
        <v-btn
          v-for="view in taxonomyViews"
          :key="view.key"
          depressed
          :class="{ active: currentView === view.key }"
          @click="$emit('update:currentView', view.key)"
        >
          <v-icon start size="small">{{ view.icon }}</v-icon>
          {{ view.label }}
        </v-btn>
      </div>
      
      <!-- Shared Views (Attributes) -->
      <div class="view-group">
        <div class="group-header">
          Shared (All Versions)
          <v-icon size="x-small" color="warning">mdi-alert</v-icon>
        </div>
        <v-btn
          v-for="view in sharedViews"
          :key="view.key"
          depressed
          :class="{ active: currentView === view.key }"
          @click="$emit('update:currentView', view.key)"
        >
          <v-icon start size="small">{{ view.icon }}</v-icon>
          {{ view.label }}
          <v-chip size="x-small" color="info" variant="outlined" class="ml-2">ALL</v-chip>
        </v-btn>
      </div>
    </div>

    <!-- Status Sections -->
    <div class="status-sections">
      <div class="section-header">What You're Editing</div>
      
      <!-- Taxonomy Status -->
      <div class="status-card" :class="{ 'has-changes': unsavedChanges?.taxonomy }">
        <div class="status-icon">
          <v-icon :color="unsavedChanges?.taxonomy ? 'warning' : 'success'" size="small">
            {{ unsavedChanges?.taxonomy ? 'mdi-alert-circle' : 'mdi-check-circle' }}
          </v-icon>
        </div>
        <div class="status-content">
          <div class="status-title">
            <v-icon size="x-small">mdi-file-tree</v-icon>
            Taxonomy
          </div>
          <div class="status-detail">{{ backendTaxonomyName || taxonomyDisplayName }}</div>
          <div class="status-version">{{ loadedVersion || 'Not loaded' }}</div>
        </div>
        <v-btn 
          v-if="unsavedChanges?.taxonomy"
          size="x-small" 
          color="primary" 
          variant="tonal"
          @click="$emit('saveTaxonomy')"
        >
          Save
        </v-btn>
      </div>
      
      <!-- Attributes Status -->
      <div class="status-card" :class="{ 'has-changes': unsavedChanges?.attributes }">
        <div class="status-icon">
          <v-icon :color="unsavedChanges?.attributes ? 'warning' : 'success'" size="small">
            {{ unsavedChanges?.attributes ? 'mdi-alert-circle' : 'mdi-check-circle' }}
          </v-icon>
        </div>
        <div class="status-content">
          <div class="status-title">
            <v-icon size="x-small">mdi-tag-multiple</v-icon>
            Attributes
            <v-chip size="x-small" color="info" variant="outlined">SHARED</v-chip>
          </div>
          <div class="status-detail text-warning" v-if="unsavedChanges?.attributes">
            Changes affect ALL versions
          </div>
          <div class="status-version">{{ loadedVersion || 'Not loaded' }}</div>
        </div>
        <v-btn 
          v-if="unsavedChanges?.attributes"
          size="x-small" 
          color="primary" 
          variant="tonal"
          @click="$emit('saveAttributes')"
        >
          Save
        </v-btn>
      </div>
      
      <!-- GPC Mappings Status -->
      <div class="status-card" :class="{ 'has-changes': unsavedChanges?.gpcMappings }">
        <div class="status-icon">
          <v-icon :color="unsavedChanges?.gpcMappings ? 'warning' : 'success'" size="small">
            {{ unsavedChanges?.gpcMappings ? 'mdi-alert-circle' : 'mdi-check-circle' }}
          </v-icon>
        </div>
        <div class="status-content">
          <div class="status-title">
            <v-icon size="x-small">mdi-link</v-icon>
            GPC Mappings
          </div>
          <div class="status-detail">{{ backendTaxonomyName || taxonomyDisplayName }}</div>
          <div class="status-version">{{ loadedVersion || 'Not loaded' }}</div>
        </div>
        <v-btn 
          v-if="unsavedChanges?.gpcMappings"
          size="x-small" 
          color="primary" 
          variant="tonal"
          @click="$emit('saveGpcMappings')"
        >
          Save
        </v-btn>
      </div>
      
      <!-- Frontend Status -->
      <div class="status-card" :class="{ 'has-changes': unsavedChanges?.frontend }">
        <div class="status-icon">
          <v-icon :color="unsavedChanges?.frontend ? 'warning' : 'success'" size="small">
            {{ unsavedChanges?.frontend ? 'mdi-alert-circle' : 'mdi-check-circle' }}
          </v-icon>
        </div>
        <div class="status-content">
          <div class="status-title">
            <v-icon size="x-small">mdi-web</v-icon>
            Frontend Rules
          </div>
          <div class="status-detail">{{ frontendFilename || 'frontend' }}</div>
          <div class="status-version">{{ loadedVersion || 'Not loaded' }}</div>
          <div class="status-orphans" v-if="orphanCount > 0">
            <v-icon size="x-small" color="warning">mdi-alert</v-icon>
            {{ orphanCount }} orphaned rules
          </div>
        </div>
        <v-btn 
          v-if="unsavedChanges?.frontend"
          size="x-small" 
          color="primary" 
          variant="tonal"
          @click="$emit('saveFrontendChanges')"
        >
          Save
        </v-btn>
      </div>
    </div>

    <!-- Version Loading Section -->
    <div class="version-section">
      <div class="section-header">Version Management</div>
      
      <div class="d-flex align-center gap-2">
        <v-autocomplete
          dense
          hide-details
          clearable
          style="flex: 1"
          :items="formattedVersions"
          :model-value="loadVersion"
          label="load version"
          placeholder="type to search versions"
          @update:modelValue="$emit('update:loadVersion', $event)"
          item-title="text"
          item-value="value"
          :menu-props="{ dense: true, style: 'font-size: 11px' }"
          class="version-dropdown"
        />
        <v-btn icon size="small" @click="$emit('refreshVersions')">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>

      <v-switch
        class="mt-2 pt-0"
        dense
        hide-details
        label="show backup versions"
        :model-value="showBackupVersions"
        @update:modelValue="$emit('toggleShowBackupVersions')"
      />

      <v-btn block class="mt-2" color="primary" variant="outlined" @click="$emit('loadTaxonomy')">
        <v-icon start>mdi-download</v-icon>
        Load Version
      </v-btn>

      <v-text-field
        dense
        hide-details
        class="mt-3"
        label="save as version"
        placeholder="Leave empty to save to current version"
        :model-value="saveVersion"
        @update:modelValue="$emit('update:saveVersion', $event)"
      />
      <span class="text-caption text-grey text-center version-description-text">{{ props.currentVersionDescription }}</span>
    </div>

    <!-- Action Buttons -->
    <div class="action-buttons">
      <v-btn 
        block 
        color="primary" 
        :disabled="!hasAnyUnsavedChanges"
        @click="$emit('saveChanges')"
      >
        <v-icon start>mdi-content-save</v-icon>
        {{ props.selectedTaxonomy?.startsWith('frontend') ? 'Save Frontend Changes' : 'Save All Changes' }}
      </v-btn>
      
      <v-btn 
        block 
        class="mt-2" 
        color="secondary" 
        variant="outlined"
        @click="$emit('publishTaxonomy')"
      >
        <v-icon start>mdi-publish</v-icon>
        Publish...
      </v-btn>
      
      <v-btn 
        block 
        class="mt-2" 
        variant="text"
        @click="$emit('downloadTaxonomy')"
      >
        <v-icon start>mdi-download</v-icon>
        Download
      </v-btn>
      
      <v-divider class="my-3"></v-divider>
      
      <v-btn 
        block 
        color="orange-darken-1" 
        variant="outlined"
        size="small"
        @click="$emit('syncQaVersion')"
      >
        <v-icon start size="small">mdi-sync</v-icon>
        Reset QA to Working Version
      </v-btn>
      <p class="text-caption text-grey mt-1 text-center">
        Overwrites QA Version for safe testing
      </p>
    </div>
    
    <!-- System Health Indicator -->
    <div class="system-health" v-if="systemHealth">
      <v-chip 
        size="small" 
        :color="systemHealth.status === 'healthy' ? 'success' : 'warning'"
        variant="tonal"
      >
        <v-icon start size="small">
          {{ systemHealth.status === 'healthy' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
        </v-icon>
        System {{ systemHealth.status }}
      </v-chip>
      <v-chip 
        v-if="v2EndpointsEnabled"
        size="small" 
        color="info"
        variant="tonal"
        class="ml-1"
      >
        V2 Ready
      </v-chip>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  searchQuery: String,
  currentView: String,
  loadVersion: String,
  formattedVersions: Array,
  showBackupVersions: Boolean,
  saveVersion: String,
  versionDescription: String,
  currentVersionDescription: String,
  loadedVersion: String,
  hasUnsavedChanges: Boolean,
  selectedTaxonomy: String,
  backendTaxonomyName: String,  // Always shows the backend taxonomy name (doesn't change when viewing frontend)
  taxonomyVersion: String,  // 'v1' or 'v2'
  unsavedChanges: Object,   // { taxonomy, attributes, gpcMappings, frontend }
  orphanCount: {
    type: Number,
    default: 0
  },
  systemHealth: Object,
  frontendFilename: String,
  v2EndpointsEnabled: {
    type: Boolean,
    default: false
  }
});

// Navigation view groups
const taxonomyViews = [
  { key: 'backendTaxonomyEditor', label: 'Backend Taxonomy', icon: 'mdi-file-tree' },
  { key: 'frontendTaxonomyEditor', label: 'Frontend Rules', icon: 'mdi-web' },
  { key: 'gpcMapping', label: 'GPC Mapping', icon: 'mdi-link' },
  { key: 'sizeMapping', label: 'Size Mapping', icon: 'mdi-tshirt-crew-outline' },
  { key: 'styleRules', label: 'Style Rules', icon: 'mdi-book-open-variant' },
];

const sharedViews = [
  { key: 'attributeOverview', label: 'Attribute Overview', icon: 'mdi-view-list' },
  { key: 'brands', label: 'Brands', icon: 'mdi-tag' },
  { key: 'attributeEditor', label: 'Attribute Editor', icon: 'mdi-pencil' },
  { key: 'languageVariants', label: 'Language Variants', icon: 'mdi-translate' },
];

// Computed properties
const taxonomyDisplayName = computed(() => {
  if (!props.selectedTaxonomy) return 'None selected';
  return props.selectedTaxonomy.replace('.json', '').replace(/_/g, ' ');
});

const hasAnyUnsavedChanges = computed(() => {
  // Check both the granular unsavedChanges object AND the legacy hasUnsavedChanges boolean
  if (props.hasUnsavedChanges) {
    return true;
  }
  if (props.unsavedChanges) {
    if (Object.values(props.unsavedChanges).some(v => v)) {
      return true;
    }
  }
  // Also enable save if user has entered a custom version name (to save snapshot)
  if (props.saveVersion && props.saveVersion.trim()) {
    return true;
  }
  return false;
});

const isCollapsed = ref(false);
</script>

<style scoped>
.sidebar {
  width: 320px;
  padding: 1rem;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow-y: auto;
}

.view-switcher {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin: 1rem 0;
  flex-shrink: 0;
}

.view-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.group-header {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 4px;
}

.view-group .v-btn {
  width: 100%;
  justify-content: flex-start;
  text-transform: none;
  height: 36px;
  font-size: 0.85rem;
}

.view-group .active {
  background: #2563eb;
  color: #fff;
}

.section-header {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
}

.status-sections {
  margin: 0.5rem 0;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.status-card {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  background: #f9fafb;
  border: 1px solid transparent;
}

.status-card:last-child {
  margin-bottom: 0;
}

.status-card.has-changes {
  background: #fffbeb;
  border-color: #fcd34d;
}

.status-icon {
  flex-shrink: 0;
  padding-top: 2px;
}

.status-content {
  flex-grow: 1;
  min-width: 0;
}

.status-title {
  font-weight: 600;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.status-detail {
  font-size: 0.7rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-version {
  font-size: 0.65rem;
  color: #9ca3af;
}

.status-orphans {
  font-size: 0.65rem;
  color: #d97706;
  display: flex;
  align-items: center;
  gap: 2px;
}

.version-section {
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-bottom: 0.5rem;
}

.action-buttons {
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.system-health {
  margin-top: 0.5rem;
  text-align: center;
  display: flex;
  justify-content: center;
  gap: 4px;
  flex-wrap: wrap;
}

.hamburger-button {
  position: fixed;
  
  /* Display over header to prevent overlap with page items */
  left: 25px;
  top: 15px;
  
  /* Z-index is needed because otherwise the header covers the button */
  z-index: 99999;
}

/* clamp the entire text-field wrapper */
:deep(.fixed-search) {
  height: 48px !important;
  min-height: 48px !important;
  max-height: 48px !important;
  flex: 0 0 auto !important;
  overflow: hidden;
}

/* version dropdown font tweaks */
:deep(.version-dropdown .v-field__input),
:deep(.version-dropdown .v-list-item-title),
:deep(.version-dropdown .v-autocomplete__content) {
  font-size: 11px !important;
  line-height: 1.2 !important;
}

/* mobile tweaks */
@media (max-height: 700px) {
  .sidebar {
    padding: 0.5rem;
  }

  .view-switcher {
    margin: 0.5rem 0;
    gap: 0.5rem;
  }

  .status-sections {
    padding: 0.5rem;
  }
  
  .status-card {
    padding: 0.4rem;
  }

  .version-section {
    padding: 0.5rem;
  }
}

.text-warning {
  color: #d97706 !important;
}

.version-description-text {
  font-size: 0.65rem !important;
}
</style>
